import { useEffect, useMemo, useState } from 'react'

import ChartTransitionWrapper from '@/components/atomic/chart-transition-wrapper'
import { BodySubtext } from '@/components/atomic/text'

import { SegmentChart as BaseSegmentChart, type ChartConfig } from './base'
import { SegmentChartSkeleton } from './skeleton'

export interface SegmentChartProps {
  chartData: Record<string, number>
  chartConfig: ChartConfig
  title?: string
  width?: number
  fullWidth?: boolean
  showLegend?: boolean
  targetPercentage?: number
  differencePercentage?: number
  withinBounds?: boolean
  targetLabel?: string
  preserveOriginalOrder?: boolean
  /**
   * Whether to enable smooth staggered animations for legend items
   * @default false
   */
  enableAnimation?: boolean
  /**
   * Whether to show loading skeleton animation
   * @default false
   */
  isLoading?: boolean
}

const BASE_HEIGHT = 64

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function SegmentChart({
  chartData,
  chartConfig,
  title,
  width,
  fullWidth,
  showLegend,
  targetPercentage,
  differencePercentage,
  withinBounds,
  targetLabel,
  preserveOriginalOrder,
  enableAnimation = false,
  isLoading = false,
}: SegmentChartProps) {
  // Redraw chart to recalculate everything when the chartConfig changes
  const [chartKey, setChartKey] = useState(0)

  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  useEffect(() => {
    // Force redraw when chartConfig changes
    setChartKey((prev) => prev + 1)
  }, [chartConfig])

  const baseHeight = useMemo(() => {
    // If title is absent, subtract 16px from the base height
    return BASE_HEIGHT + (!title ? -16 : 0)
  }, [title])

  // This is used to resize the parent container when the view more / show less
  // button is clicked inside the chart legend
  const [parentContainerHeight, setParentContainerHeight] = useState(baseHeight)

  // Ensure data normalization when polling updates come in
  const normalizedChartData = useMemo(() => {
    // Filter out zero values and ensure values add up to 100%
    const filteredData = Object.fromEntries(
      Object.entries(chartData).filter(([_, value]) => value > 0),
    )

    // If no data or all zeros, return empty object
    if (Object.keys(filteredData).length === 0) {
      return {}
    }

    // Normalize the values to ensure they sum to 100%
    const sum = Object.values(filteredData).reduce((acc, val) => acc + val, 0)
    if (sum === 0) return {}

    if (Math.abs(sum - 100) < 0.1) {
      // If already close to 100%, don't adjust
      return filteredData
    }

    // Scale values to add up to 100%
    return Object.fromEntries(
      Object.entries(filteredData).map(([key, value]) => [
        key,
        (value / sum) * 100,
      ]),
    )
  }, [chartData])

  return (
    <div
      style={{
        minHeight: parentContainerHeight,
        maxHeight: parentContainerHeight,
      }}
    >
      {title && (
        <BodySubtext className="text-secondary text-left">{title}</BodySubtext>
      )}
      <ChartTransitionWrapper
        isLoading={isLoading ?? false}
        height="h-[112px] min-h-[112px]"
        loadingContent={
          <SegmentChartSkeleton segmentCount={6} animationDuration={2000} />
        }
      >
        <BaseSegmentChart
          key={chartKey}
          chartData={normalizedChartData}
          chartConfig={chartConfig}
          width={width}
          baseHeight={baseHeight}
          fullWidth={fullWidth}
          showLegend={showLegend}
          targetPercentage={targetPercentage}
          differencePercentage={differencePercentage}
          withinBounds={withinBounds}
          targetLabel={targetLabel}
          preserveOriginalOrder={preserveOriginalOrder}
          enableAnimation={enableAnimation}
          setParentContainerHeight={setParentContainerHeight}
        />
      </ChartTransitionWrapper>
    </div>
  )
}
